// Закрепление планшета за точкой (T-040, решение Д9).
//
// Ключ планшета — дверь для входа сотрудника по PIN. Сервер принимает PIN ТОЛЬКО вместе
// с живым ключом: четыре цифры без него перебирались бы за минуты.
//
// Ключ помечен `lifetime: 'device'` (preferenceKeys.ts): выход повара на пересменке
// его не стирает — иначе после «Выйти» планшет показал бы форму логина и пароля,
// ровно то, от чего задача уходит. Стирается он владельцем: при отзыве с сервера
// (`kiosk_revoked`) и при откреплении вручную.

import { Preferences } from '@capacitor/preferences';

import { PREFERENCE_KEYS } from '@/shared/storage/preferenceKeys';

export type KioskBinding = {
  /** Сырой ключ планшета (`ksk_…`). Существует только здесь и в ответе на закрепление. */
  readonly token: string;
  readonly id: string;
  readonly name: string;
  readonly locationName: string;
  /**
   * Точка закрепления. Лента и уведомления Windows на закреплённом планшете — её, а не
   * основной точки вошедшего (№10, 2026-09-30). У закреплённых до 0.2.41 поля нет —
   * точку тогда ищут по `locationName`.
   */
  readonly locationId?: string;
};

export async function loadKioskBinding(): Promise<KioskBinding | null> {
  const { value } = await Preferences.get({ key: PREFERENCE_KEYS.kioskBinding });
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value) as KioskBinding;
  } catch {
    // Испорченная запись — не повод ронять экран входа: без закрепления планшет
    // просто показывает обычную форму, а менеджер закрепит его заново.
    await clearKioskBinding();
    return null;
  }
}

export async function saveKioskBinding(binding: KioskBinding): Promise<void> {
  await Preferences.set({ key: PREFERENCE_KEYS.kioskBinding, value: JSON.stringify(binding) });
  notifyKioskBinding();
}

export async function clearKioskBinding(): Promise<void> {
  await Preferences.remove({ key: PREFERENCE_KEYS.kioskBinding });
  notifyKioskBinding();
}

// Закрепили или открепили — кто следит за точкой планшета (лента), узнаёт сразу,
// а не после перезапуска.
const listeners = new Set<() => void>();

export function subscribeKioskBinding(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyKioskBinding(): void {
  for (const listener of listeners) listener();
}
